/**
 * VenueComboBox — unified venue picker for the admin event creation form.
 *
 * Replaces the old free-text "Venue Name" input + Places-only autocomplete.
 * Search semantics:
 *   1. As the admin types (>= 2 chars), we hit
 *      GET /api/cities/{city}/venues/search/lookup
 *      to surface verified venues that already live in our DB. They
 *      appear FIRST with a green ✔ badge — keeps the marketplace
 *      canonical and avoids duplicates.
 *   2. In parallel we hit GET /api/places/autocomplete to surface
 *      Google Places suggestions. They appear below the local hits.
 *   3. Picking a verified venue → fill name/address/city/state/zip
 *      from the DB record.
 *   4. Picking a Places suggestion → POST
 *      /api/admin/venues/import-from-places to enrich + persist (CDN
 *      photos + AI description + lat/lng + verified=true), then fill
 *      the form from the freshly-created record. Every selected venue
 *      ends up in our DB verified, so the next admin creating an event
 *      at the same place gets the local hit instead of paying Places.
 *
 * The free-text input is always editable — if the admin doesn't pick
 * any suggestion (e.g. brand-new pop-up location), they can still type
 * freely and the form continues to work as before.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  MapPin, Loader2, ExternalLink, CheckCircle2, Plus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VenueComboBox({ formData, setFormData, token }) {
  const [query, setQuery] = useState(formData.venue_name || '');
  const [localVenues, setLocalVenues] = useState([]);
  const [placesResults, setPlacesResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null); // place_id being imported
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);
  const wrapRef = useRef(null);

  // Mirror parent venue_name into local query if it changes externally
  // (e.g. AI Generate prefill).
  useEffect(() => { setQuery(formData.venue_name || ''); }, [formData.venue_name]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Parallel search: our DB + Google Places. Local hits win the top of
  // the dropdown; Places fills the rest. Debounced to 280 ms.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const q = query.trim();
    if (q.length < 2) {
      setLocalVenues([]);
      setPlacesResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      const city = (formData.venue_city || formData.city || 'denver').toLowerCase();
      try {
        const [localRes, placesRes] = await Promise.allSettled([
          axios.get(`${API}/cities/${city}/venues/search/lookup`, { params: { q, limit: 6 } }),
          axios.get(`${API}/places/autocomplete`, { params: { q } }),
        ]);
        setLocalVenues(localRes.status === 'fulfilled'
          ? (localRes.value.data?.venues || []) : []);
        setPlacesResults(placesRes.status === 'fulfilled'
          ? (placesRes.value.data?.results || []) : []);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [query, formData.venue_city, formData.city]);

  // Pick a verified venue from our DB — fast path, no extra API call.
  const pickLocal = (v) => {
    setFormData({
      ...formData,
      venue_name:    v.name || '',
      venue_id:      v.venue_id || null,
      venue_slug:    v.slug || null,
      venue_address: v.address || formData.venue_address || '',
      venue_city:    v.city || formData.venue_city || '',
      venue_state:   v.state || formData.venue_state || '',
      venue_zip:     v.zip || formData.venue_zip || '',
      venue_lat:     v.lat ?? formData.venue_lat ?? null,
      venue_lng:     v.lng ?? formData.venue_lng ?? null,
      google_place_id: v.google_place_id || formData.google_place_id || null,
    });
    setQuery(v.name);
    setOpen(false);
  };

  // Pick a Google Places suggestion → import-and-persist, then fill the
  // form from the persisted row. We show an inline spinner on the
  // suggestion that's importing so the admin knows what's happening.
  const pickPlace = async (place) => {
    setImporting(place.place_id);
    try {
      const res = await axios.post(`${API}/admin/venues/import-from-places`,
        { place_id: place.place_id },
        { headers: { Authorization: `Bearer ${token}` } });
      const v = res.data?.venue || {};
      setFormData({
        ...formData,
        venue_name:    v.name || place.name || '',
        venue_id:      v.venue_id || null,
        venue_slug:    v.slug || null,
        venue_address: v.address || place.formatted_address || '',
        venue_city:    v.city || place.city || '',
        venue_state:   v.state || place.state || '',
        venue_zip:     v.zip || place.zip || '',
        venue_lat:     v.lat ?? place.lat ?? null,
        venue_lng:     v.lng ?? place.lng ?? null,
        google_place_id: v.google_place_id || place.place_id || null,
      });
      setQuery(v.name || place.name);
      setOpen(false);
    } catch (err) {
      // Soft-fail: still let the admin proceed with raw Places fields so
      // they're not blocked by a 5xx on the import step.
      setFormData({
        ...formData,
        venue_name:    place.name || '',
        venue_address: place.street || place.formatted_address || '',
        venue_city:    place.city || formData.venue_city || '',
        venue_state:   place.state || formData.venue_state || '',
        venue_zip:     place.zip || formData.venue_zip || '',
        google_place_id: place.place_id || null,
      });
      setQuery(place.name);
      setOpen(false);
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className="space-y-2" ref={wrapRef} data-testid="venue-combobox">
      <Label>Venue *</Label>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // Mirror raw typing so the form still works if no
            // suggestion is picked (rare — pop-up at someone's house).
            setFormData({
              ...formData,
              venue_name: e.target.value,
              // Clearing the input clears the verified-venue binding
              ...(e.target.value !== formData.venue_name
                ? { venue_id: null, venue_slug: null }
                : {}),
            });
          }}
          onFocus={() => (localVenues.length || placesResults.length) && setOpen(true)}
          placeholder="Start typing a venue name…"
          data-testid="venue-name-input"
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {open && (localVenues.length > 0 || placesResults.length > 0) && (
          <div
            className="absolute z-30 left-0 right-0 mt-1 bg-popover border rounded-lg shadow-xl overflow-hidden max-h-96 overflow-y-auto"
            data-testid="venue-suggestions"
          >
            {/* ── Verified venues from our DB ── */}
            {localVenues.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border-b border-border/40">
                  Verified venues
                </div>
                {localVenues.map((v) => (
                  <button
                    key={v.venue_id}
                    type="button"
                    onClick={() => pickLocal(v)}
                    className="w-full text-left p-3 hover:bg-muted/60 border-b border-border/40 last:border-0 flex gap-2 items-start"
                    data-testid={`venue-local-${v.venue_id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{v.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {v.venue_type ? `${v.venue_type} · ` : ''}
                        {v.neighborhood || v.city}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* ── Google Places fallback ── */}
            {placesResults.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-indigo-400 bg-indigo-500/10 border-b border-border/40">
                  New venues from Google
                </div>
                {placesResults.map((p) => {
                  const isImporting = importing === p.place_id;
                  return (
                    <button
                      key={p.place_id}
                      type="button"
                      onClick={() => !isImporting && pickPlace(p)}
                      disabled={isImporting}
                      className="w-full text-left p-3 hover:bg-muted/60 border-b border-border/40 last:border-0 flex gap-2 items-start disabled:opacity-60"
                      data-testid={`venue-place-${p.place_id}`}
                    >
                      {isImporting ? (
                        <Loader2 className="h-3.5 w-3.5 mt-0.5 text-indigo-400 flex-shrink-0 animate-spin" />
                      ) : (
                        <Plus className="h-3.5 w-3.5 mt-0.5 text-indigo-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{p.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{p.formatted_address}</div>
                        {(p.rating || p.user_ratings_total) ? (
                          <div className="text-[10px] text-amber-500 mt-0.5">
                            ★ {p.rating?.toFixed(1)} · {p.user_ratings_total} reviews
                          </div>
                        ) : null}
                        {isImporting && (
                          <div className="text-[10px] text-indigo-300 mt-0.5">
                            Importing & verifying via Google…
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
                <div className="text-[10px] text-muted-foreground px-3 py-1.5 bg-muted/30 flex items-center gap-1">
                  <span>Powered by Google Places</span>
                  <ExternalLink className="h-2.5 w-2.5" />
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {formData.venue_id ? (
        <p className="text-[11px] text-emerald-400 flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3" />
          Linked to verified venue · address &amp; coordinates pulled automatically
        </p>
      ) : (
        <p className="text-[10px] text-muted-foreground">
          Picking a result auto-fills address, city, state, zip and verifies the venue.
        </p>
      )}
    </div>
  );
}
